import { action, makeObservable, observable, runInAction } from 'mobx';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';

export type RppPrizeCouponLanguage = 'en' | 'fr';

/**
 * RPP promos come in two flavours:
 * - `coupon`: a prize/inventory coupon (activation code + quantity, may be private).
 * - `in_store`: a public in-store display promo with no prize inventory.
 * The backend defaults to `coupon` when `type` is omitted.
 */
export type RppPrizeCouponType = 'coupon' | 'in_store';

/** Human-readable subject for success toasts, so `in_store` promos don't read as coupons. */
function toastSubject(type: RppPrizeCouponType): string {
    return type === 'in_store' ? 'RPP in-store promo' : 'RPP prize coupon';
}

/**
 * A localized content item for an RPP prize coupon. Only `en` is required by the
 * backend; `fr` is optional. Every item that is sent must carry a non-empty title.
 */
export interface RppPrizeCouponItem {
    language: RppPrizeCouponLanguage;
    title: string;
    subTitle?: string | null;
    description?: string | null;
    thumbnail?: string | null;
    background?: string | null;
}

export interface RppPrizeCouponProvince {
    id: number;
    name: string;
}

/**
 * An RPP promo as returned by the backend. These are regular promotions
 * (`isForRpp=true`) that are deliberately excluded from the `/promotions`
 * endpoints, hence their own CMS page. They are either prize coupons
 * (`type='coupon'`) or public in-store display promos (`type='in_store'`).
 *
 * For `in_store` promos the coupon-only fields are nullable: `activationCode`,
 * `quantity` and `remainingQuantity` are `null`, and `grantedQuantity` may be `0`
 * (it does not represent inventory usage for in-store promos).
 */
export interface RppPrizeCoupon {
    id: number;
    type: RppPrizeCouponType;
    isActive: boolean;
    isPrivate: boolean | null;
    isForRpp: boolean;
    activationCode: string | null;
    quantity: number | null;
    grantedQuantity: number;
    activatedQuantity?: number | null;
    remainingQuantity: number | null;
    startDate: string;
    endDate: string;
    timezone: string;
    categoryId: number | null;
    promotionalOfferId: number | null;
    discountValue: number | null;
    rebateAmount: number;
    originalPrice: number;
    provinceIds: number[];
    provinces: RppPrizeCouponProvince[];
    items: RppPrizeCouponItem[];
    createdAt: string;
    updatedAt: string;
}

export interface RppPrizeCouponListResponse {
    page: number;
    perPage: number;
    total: number;
    results: RppPrizeCoupon[];
}

/**
 * Body sent to `POST /rpp-prize-coupons`. We also send the full body to
 * `PUT /rpp-prize-coupons/{id}` (a valid superset of the partial update contract).
 * The server forces `isForRpp=true`, so that flag is never sent from the client.
 *
 * Coupon-only fields (`isPrivate`, `activationCode`, `quantity`) are optional: they
 * are sent for `type='coupon'` and omitted for `type='in_store'` public promos.
 */
export interface RppPrizeCouponPayload {
    type: RppPrizeCouponType;
    startDate: string;
    endDate: string;
    timezone: string;
    isActive: boolean;
    isPrivate?: boolean;
    activationCode?: string;
    quantity?: number;
    categoryId: number;
    promotionalOfferId: number | null;
    provinceIds: number[];
    items: RppPrizeCouponItem[];
    discountValue: number | null;
    rebateAmount: number;
    originalPrice: number;
}

/** List query params, expressed in the CMS's 0-based page convention. */
export type RppPrizeCouponSearchParams = {
    page?: number;
    perPage?: number;
    q?: string;
    type?: RppPrizeCouponType;
};

export class RppPrizeCouponStore extends WithNetworkConcurrency {
    @observable coupons: RppPrizeCouponListResponse | null = null;
    @observable isFetchingList = false;
    @observable isFetchingCoupon = false;
    @observable isSaving = false;
    @observable isDeleting = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    async fetchList(
        this: RppPrizeCouponStore,
        searchParams: RppPrizeCouponSearchParams = {}
    ) {
        const tag = this.getTag();
        this.isFetchingList = true;

        // The CMS paginates with a 0-based `page`; the coupons API is 1-based.
        const { page, perPage, q, type } = searchParams;
        try {
            const response = await this.rootStore.makeNetworkCall<
                RppPrizeCouponListResponse
            >({
                method: HttpMethod.GET,
                url: '/rpp-prize-coupons',
                params: {
                    page: (page ?? 0) + 1,
                    perPage,
                    q: q || undefined,
                    type: type || undefined,
                },
            });

            if (!response.err && response.data && this.isLatestTag(tag)) {
                const data = response.data;
                runInAction(() => {
                    this.coupons = data;
                });
            }
        } finally {
            runInAction(() => {
                this.isFetchingList = false;
            });
        }
    }

    @action
    async fetchCoupon(
        this: RppPrizeCouponStore,
        id: string
    ): Promise<RppPrizeCoupon | null> {
        this.isFetchingCoupon = true;
        try {
            const response = await this.rootStore.makeNetworkCall<
                RppPrizeCoupon
            >({
                method: HttpMethod.GET,
                url: `/rpp-prize-coupons/${id}`,
            });

            return !response.err && response.data ? response.data : null;
        } finally {
            runInAction(() => {
                this.isFetchingCoupon = false;
            });
        }
    }

    @action
    async createCoupon(
        this: RppPrizeCouponStore,
        data: RppPrizeCouponPayload
    ): Promise<RppPrizeCoupon | null> {
        this.isSaving = true;
        const response = await this.rootStore.makeNetworkCall<RppPrizeCoupon>({
            method: HttpMethod.POST,
            url: '/rpp-prize-coupons',
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess(
                `${toastSubject(data.type)} created.`
            );
        }

        runInAction(() => {
            this.isSaving = false;
        });
        return !response.err && response.data ? response.data : null;
    }

    @action
    async updateCoupon(
        this: RppPrizeCouponStore,
        id: number,
        data: RppPrizeCouponPayload
    ): Promise<RppPrizeCoupon | null> {
        this.isSaving = true;
        const response = await this.rootStore.makeNetworkCall<RppPrizeCoupon>({
            method: HttpMethod.PUT,
            url: `/rpp-prize-coupons/${id}`,
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess(
                `${toastSubject(data.type)} updated.`
            );
        }

        runInAction(() => {
            this.isSaving = false;
        });
        return !response.err && response.data ? response.data : null;
    }

    @action
    async deleteCoupon(
        this: RppPrizeCouponStore,
        id: number,
        type: RppPrizeCouponType = 'coupon'
    ) {
        this.isDeleting = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/rpp-prize-coupons/${id}`,
        });

        if (!response.err) {
            NotificationManager.showSuccess(`${toastSubject(type)} deleted.`);
        }

        runInAction(() => {
            this.isDeleting = false;
        });
        return response;
    }
}
